import React, { useState, useEffect } from "react";
import api from "../../api";
import Select from "react-select";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from "@mui/material";
import {
  FaMoneyBillWave,
  FaChalkboard,
  FaEdit,
  FaSearch,
  FaTimesCircle,
  FaSave,
  FaPlusCircle,
} from "react-icons/fa";

const FeesStructureListPage = () => {
  const [fees, setFees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [newFee, setNewFee] = useState({
    class_id: "",
    fee_type: "",
    amount: "",
    billing_cycle: "",
  });
  const [msg, setMsg] = useState(null);

  const schoolId = localStorage.getItem("school_id");

  // API endpoints
  const feesURL = `/fees_structure/list`;
  const addURL = `/fees_structure/add`;

  // Fetch class list
  // useEffect(() => {
  //   fetch(classURL)
  //     .then((res) => res.json())
  //     .then((data) => setClasses(data.data || []))
  //     .catch((err) => console.error("Error fetching class:", err));
  // }, []);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await api.get("/get-classes");
  
        if (res.data && res.data.success) {
          setClasses(res.data.data); // backend e data array ache
        } else {
          setClasses([]);
          console.warn("Unexpected response:", res.data);
        }
      } catch (error) {
          setClasses([]);
        console.error("Error fetching classes:", error);
      }
    };
  
    fetchClasses();
  }, []);

  // Fetch fees list
  const fetchFees = async (classId = null) => {
    setLoading(true);
    try {
      const url = classId
        ? `${feesURL}?school_id=${schoolId}&class_id=${classId}`
        : `${feesURL}?school_id=${schoolId}`;
      const res = await api.get(url);
      if (res.data?.status) setFees(res.data.data || []);
      else setFees([]);
    } catch (err) {
      console.error("Error fetching fees:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFees();
  }, []);

  const handleFilter = () => {
    fetchFees(selectedClass ? selectedClass.value : null);
  };

  const openEditModal = (fee) => {
    setSelectedFee(fee);
    setEditModal(true);
  };

  const handleEditChange = (key, value) => {
    setSelectedFee({ ...selectedFee, [key]: value });
  };

  const handleSaveEdit = async () => {
    try {
      const res = await api.post(`/fees_structure/update/${selectedFee.id}`, selectedFee);
      if (res.data.status) {
        setMsg({ type: "success", text: "Updated successfully!" });
        fetchFees(selectedClass ? selectedClass.value : null);
        setEditModal(false);
      } else {
        setMsg({ type: "error", text: res.data.message });
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Failed to update.",
      });
    }
  };

  const handleAddFee = async () => {
    if (!newFee.class_id || !newFee.fee_type || !newFee.amount || !newFee.billing_cycle) {
      alert("Please fill all required fields.");
      return;
    }
    try {
      const payload = {
        school_id: schoolId,
        class_id: newFee.class_id,
        fee_type: newFee.fee_type,
        amount: newFee.amount,
        billing_cycle: newFee.billing_cycle,
      };

      const res = await api.post(addURL, payload);
      if (res.data.status) {
        setMsg({ type: "success", text: "Fee added successfully!" });
        setAddModal(false);
        setNewFee({ class_id: "", fee_type: "", amount: "", billing_cycle: "" });
        fetchFees(selectedClass ? selectedClass.value : null);
      } else {
        setMsg({ type: "error", text: res.data.message });
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Failed to add fee.",
      });
    }
  };

  return (
    <Box sx={{ background: "#f4f6f8", minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          maxWidth: 1200,
          mx: "auto",
          p: 3,
          borderRadius: 3,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#004aad",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FaChalkboard /> Class-wise Fees Structure
          </Typography>

          <Button
            variant="contained"
            color="success"
            startIcon={<FaPlusCircle />}
            onClick={() => setAddModal(true)}
          >
            Add New Fee
          </Button>
        </Box>

        {/* Filter Section */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Select
              options={classes.map((c) => ({
                value: c.id,
                label: c.class_name,
              }))}
              value={selectedClass}
              onChange={(selected) => setSelectedClass(selected)}
              placeholder="Filter by Class"
              isClearable
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFilter}
            startIcon={<FaSearch />}
            sx={{ px: 3 }}
          >
            Filter
          </Button>
        </Box>

        {/* Fee List Table */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#0077cc",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <FaMoneyBillWave /> Fees List
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : fees.length === 0 ? (
          <Typography color="textSecondary" sx={{ mt: 2 }}>
            No fee records found.
          </Typography>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: "#004aad" }}>
                <TableRow>
                  {[
                    "ID",
                    "Class",
                    "Fee Type",
                    "Amount (₹)",
                    "Transport",
                    "Billing Cycle",
                    "Created At",
                    "Action",
                  ].map((head) => (
                    <TableCell key={head} sx={{ color: "#ffffff", fontWeight: 600 }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fees.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell>{f.id}</TableCell>
                    <TableCell>{f.class_name}</TableCell>
                    <TableCell>{f.fee_type}</TableCell>
                    <TableCell>{f.amount}</TableCell>
                    <TableCell>{f.is_transport === "1" ? "Yes" : "No"}</TableCell>
                    <TableCell>{f.billing_cycle}</TableCell>
                    <TableCell>{new Date(f.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="info"
                        size="small"
                        startIcon={<FaEdit />}
                        onClick={() => openEditModal(f)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Modal */}
      <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#004aad" }}>
          ✏️ Edit Fee Structure
        </DialogTitle>
        <DialogContent>
          {selectedFee && (
            <>
              <TextField
                fullWidth
                label="Fee Type"
                margin="dense"
                value={selectedFee.fee_type}
                onChange={(e) => handleEditChange("fee_type", e.target.value)}
              />
              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                margin="dense"
                value={selectedFee.amount}
                onChange={(e) => handleEditChange("amount", e.target.value)}
              />
              <TextField
                select
                fullWidth
                label="Billing Cycle"
                margin="dense"
                value={selectedFee.billing_cycle}
                onChange={(e) => handleEditChange("billing_cycle", e.target.value)}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            variant="outlined"
            startIcon={<FaTimesCircle />}
            onClick={() => setEditModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<FaSave />}
            onClick={handleSaveEdit}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Fee Modal */}
      <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#2e7d32" }}>
          ➕ Add New Fee Structure
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Select Class"
              margin="dense"
              value={newFee.class_id}
              onChange={(e) => setNewFee({ ...newFee, class_id: e.target.value })}
            >
              <MenuItem value="">Select Class</MenuItem>
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.class_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Fee Type"
              margin="dense"
              value={newFee.fee_type}
              onChange={(e) => setNewFee({ ...newFee, fee_type: e.target.value })}
            />
            <TextField
              fullWidth
              label="Amount (₹)"
              type="number"
              margin="dense"
              value={newFee.amount}
              onChange={(e) => setNewFee({ ...newFee, amount: e.target.value })}
            />

            <TextField
              select
              fullWidth
              label="Billing Cycle"
              margin="dense"
              value={newFee.billing_cycle}
              onChange={(e) => setNewFee({ ...newFee, billing_cycle: e.target.value })}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            variant="outlined"
            startIcon={<FaTimesCircle />}
            onClick={() => setAddModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            startIcon={<FaSave />}
            onClick={handleAddFee}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeesStructureListPage;
